/**
 * Admin → Rates (items inside a rate card).
 *
 * Per ADR-008 each rate carries BOTH the carrier (provider_id) and the
 * price (buy_cost + sell_price). The dispatch path traverses the
 * tenant's assigned rate cards in cascade order, longest-prefix-match
 * within each, strategy tiebreak.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';

export type RateMessageType = 'marketing' | 'transactional' | '2fa';

export interface Rate {
  id: number;
  rate_card_id: number;
  /** Operator-facing label like "US — Bandwidth Florida". Optional. */
  name?: string;
  /** Free-form digits or `*` for catch-all. NOT NULL. */
  prefix: string;
  message_type?: RateMessageType;
  /**
   * Carrier identity. Present only on platform-owned cards, where the
   * operator picks the carrier by hand. On a reseller card the API omits
   * it: the reseller authors one line per (prefix, message_type) and the
   * backend fans it out across the carriers its wholesale card exposes.
   */
  provider_id?: number;
  provider_name?: string;
  buy_cost: number;
  sell_price: number;
  /** Computed: sell_price - buy_cost. Returned by the API for convenience. */
  margin: number;
  priority: number;
  effective_from: string;
  effective_to?: string;
  is_active: boolean;
  notes?: string;
  updated_at: string;
  created_at: string;
}

export interface RatesResponse {
  items: Rate[];
}

export interface UpsertRateInput {
  name?: string;
  prefix: string;
  message_type?: RateMessageType | '';
  /** Omit on reseller cards — the backend derives it. */
  provider_id?: number;
  /** Omit on reseller cards — the backend takes it from the wholesale card. */
  buy_cost?: number;
  sell_price: number;
  priority?: number;
  /** RFC3339; omit to default to NOW() server-side. */
  effective_from?: string;
  effective_to?: string;
  is_active?: boolean;
  notes?: string;
}

const ratesKey = (cardId: number) => ['admin', 'rate-cards', cardId, 'rates'] as const;

export function useRates(cardId: number) {
  return useQuery({
    queryKey: ratesKey(cardId),
    queryFn: () => api.get<RatesResponse>(`/v1/admin/rate-cards/${cardId}/rates`),
    staleTime: 30_000,
    enabled: cardId > 0,
  });
}

export function useUpsertRate(cardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpsertRateInput) =>
      api.put<Rate>(`/v1/admin/rate-cards/${cardId}/rates`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ratesKey(cardId) });
      void qc.invalidateQueries({ queryKey: ['admin', 'rate-cards'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'rates'] });
    },
  });
}

/**
 * Cutover (PR 4 of billing sequence). Atomically schedules a price
 * change effective at a future date: caps the currently-active matching
 * rate's effective_to and inserts the new rate with effective_from =
 * cutover. Same wholesale-floor validation as `useUpsertRate`. Returns
 * `{ scheduled }` — the count of new rates inserted.
 */
export interface CutoverRateLine {
  name?: string;
  prefix: string;
  message_type?: RateMessageType | '';
  /** Omit on reseller cards — the backend derives it. */
  provider_id?: number;
  /** Omit on reseller cards — the backend takes it from the wholesale card. */
  buy_cost?: number;
  sell_price: number;
  priority?: number;
  notes?: string;
}

export interface CutoverInput {
  /** RFC3339 timestamp — the moment the new rates take effect. */
  effective_from: string;
  rates: CutoverRateLine[];
}

export interface CutoverResponse {
  /** Number of new rates inserted (= rates.length in the request). */
  scheduled: number;
  /** Number of currently-active rates that got effective_to set to
   *  the cutover. 0 means none of the input shapes had an incumbent
   *  — the operator probably meant a different prefix/carrier. */
  capped: number;
}

export function useRatesCutover(cardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CutoverInput) =>
      api.post<CutoverResponse>(`/v1/admin/rate-cards/${cardId}/rates/cutover`, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ratesKey(cardId) });
      void qc.invalidateQueries({ queryKey: ['admin', 'rate-cards'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'rates'] });
    },
  });
}

/**
 * Computed validity status of a rate at the current moment. The Rate
 * row itself only carries `effective_from` and `effective_to`; this
 * helper renders the relationship to NOW() so the UI can pick a badge.
 */
export type RateValidityStatus = 'active' | 'scheduled' | 'expired';

export function rateValidityStatus(rate: { effective_from: string; effective_to?: string }, now = new Date()): RateValidityStatus {
  const from = new Date(rate.effective_from);
  if (from > now) return 'scheduled';
  if (rate.effective_to) {
    const to = new Date(rate.effective_to);
    if (to <= now) return 'expired';
  }
  return 'active';
}

export function useDeleteRate(cardId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rateId: number) =>
      api.delete<void>(`/v1/admin/rate-cards/${cardId}/rates/${rateId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ratesKey(cardId) });
      void qc.invalidateQueries({ queryKey: ['admin', 'rate-cards'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'rates'] });
    },
  });
}

export interface RateWithCard extends Rate {
  rate_card_name: string;
}

export interface RatesGlobalResponse {
  items: RateWithCard[];
}

const ALL_RATES_KEY = ['admin', 'rates'] as const;

/** Cross-card list: every rate the reseller owns, with the card name joined. */
export function useAllRates() {
  return useQuery({
    queryKey: ALL_RATES_KEY,
    queryFn: () => api.get<RatesGlobalResponse>('/v1/admin/rates'),
    staleTime: 30_000,
  });
}

/** Reassigns a rate to a different card the same reseller owns. */
export function useMoveRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rateId, targetCardId }: { rateId: number; targetCardId: number }) =>
      api.patch<Rate>(`/v1/admin/rates/${rateId}`, { rate_card_id: targetCardId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ALL_RATES_KEY });
      void qc.invalidateQueries({ queryKey: ['admin', 'rate-cards'] });
    },
  });
}
